import * as pickBy from 'lodash/pickBy'
import * as isString from 'lodash/isString'
import * as size from 'lodash/size'

import { Reducer } from 'interfaces'
import { OrdersResultsReducer } from 'components/features/rewards/rewardsPane/types/store'
import {
  OrderResultsReducerChangeStateAction,
  OrderResultsReducerChangeLastOrderAction,
  OrderResultsReducerChangeLastPreviewOrderAction,
  OrderResultsReducerResetLastOrderErrorsAction,
  OrderResultsReducerNewOrderPendingAction,
  OrderResultsReducerResetLastPreviewOrderErrorsAction,
  OrderResultsReducerNewPreviewOrderPendingAction,
} from 'components/features/rewards/rewardsPane/store/actions/ordersResults'
import { CryptoRewardsPaneMode } from 'components/features/rewards/rewardsPane/types/paneState'
import { FailedResult, FieldName } from 'interfaces/errors'
import { CryptoRewardsOrderSellOrBuySuccessResponse } from 'components/features/rewards/rewardsPane/types/apiResponses'
import { BuyOrderSuccessResult, SellOrderSuccessResult } from 'components/features/rewards/rewardsPane/types/orderResult'
import { FLOATING_PANE_CRYPTO_REWARDS_BUY_MODE, FLOATING_PANE_CRYPTO_REWARDS_SELL_MODE } from 'components/features/rewards/rewardsPane/constants/paneState'
import { CRYPTO_REWARDS_ORDER_TYPE_BUY, CRYPTO_REWARDS_ORDER_TYPE_SELL } from 'components/features/rewards/rewardsPane/constants/orders'
import { CryptoRewardsOrdersListResetErrorsAction, GetCryptoRewardsOrdersListRequestAction } from 'components/features/rewards/rewardsPane/store/actions/orders'


const ORDERS_RESULTS_REDUCER_DEFAULT_STATE: OrdersResultsReducer<CryptoRewardsPaneMode> = {
}


function getOnlyExistsErrorsOrUndefined<F extends FieldName> (errors: FailedResult<F>): FailedResult<F> | undefined {
  const fieldsErrors = errors?.fieldsErrors ?? null
  const generalErrors = errors?.generalErrors ?? null
  const filteredFieldsErrorsOrNull = fieldsErrors
    ? pickBy(fieldsErrors, (item) => Array.isArray(item) && Boolean(size(item.filter(isString))))
    : null
  const filteredGeneralErrors = (generalErrors || []).filter(isString)
  const errorsFilteredOrNull: FailedResult<F> | null = errors
    ? {
      ...(filteredFieldsErrorsOrNull && Boolean(size(filteredFieldsErrorsOrNull)) ? {
        fieldsErrors: filteredFieldsErrorsOrNull,
      } : {}),
      ...(filteredGeneralErrors && Boolean(size(filteredGeneralErrors)) ? {
        generalErrors: filteredGeneralErrors,
      } : {}),
    }
    : null
  return errorsFilteredOrNull && Boolean(size(errorsFilteredOrNull))
    ? errorsFilteredOrNull
    : undefined
}

type MapOrderDetailsAPIResponseFormatToReducerFormatReturnType<
  Response extends CryptoRewardsOrderSellOrBuySuccessResponse
> = Response['type'] extends typeof CRYPTO_REWARDS_ORDER_TYPE_BUY
  ? BuyOrderSuccessResult
  : SellOrderSuccessResult

export function mapOrderDetailsAPIResponseFormatToReducerFormat<
  Response extends CryptoRewardsOrderSellOrBuySuccessResponse
> (
  orderDetailsInAPIResponseFormat: Response
): MapOrderDetailsAPIResponseFormatToReducerFormatReturnType<Response> {
  const { type } = orderDetailsInAPIResponseFormat
  switch (type) {
    case CRYPTO_REWARDS_ORDER_TYPE_BUY:
    case CRYPTO_REWARDS_ORDER_TYPE_SELL:
    default: {
      const {
        amount_usd,
        created,
        ticker,
        reward,
        execution_price,
        fees,
        filled,
        uid,
        quantity,
        status,
        taxes,
        is_autoinvest,
      } = orderDetailsInAPIResponseFormat
      const orderSuccessResult = {
        uid: uid,
        status,
        amountUSD: parseFloat(amount_usd),
        created,
        filled,
        currencyId: ticker,
        estimationReward: reward ? parseFloat(reward) : 0,
        executionPrice: execution_price ? parseFloat(execution_price) : 0,
        fees: fees ? parseFloat(fees) : 0,
        quantity: quantity ? parseFloat(quantity) : 0,
        taxes: taxes ? parseFloat(taxes) : 0,
      }
      if ((type as Response['type']) === CRYPTO_REWARDS_ORDER_TYPE_BUY) {
        const buyOrderSuccessResult: BuyOrderSuccessResult = {
          ...orderSuccessResult,
          isAutoinvest: Boolean(is_autoinvest),
          mode: FLOATING_PANE_CRYPTO_REWARDS_BUY_MODE,
        }
        return buyOrderSuccessResult as MapOrderDetailsAPIResponseFormatToReducerFormatReturnType<Response>
      }
      const sellOrderSuccessResult: SellOrderSuccessResult = {
        ...orderSuccessResult,
        mode: FLOATING_PANE_CRYPTO_REWARDS_SELL_MODE,
      }
      return sellOrderSuccessResult as MapOrderDetailsAPIResponseFormatToReducerFormatReturnType<Response>
    }
  }
}

export const ordersResultsReducer: Reducer<OrdersResultsReducer<CryptoRewardsPaneMode>> = (
  prevState: OrdersResultsReducer<CryptoRewardsPaneMode> = ORDERS_RESULTS_REDUCER_DEFAULT_STATE,
  action,
): OrdersResultsReducer<CryptoRewardsPaneMode> => {
  switch (action.type) {
    case OrderResultsReducerChangeStateAction.type:
      return {
        ...prevState,
        ...action.payload,
      }
    case OrderResultsReducerChangeLastOrderAction.type: {
      const {
        errors: lastOrderErrorsOrUndefined,
      } = action.payload
      return {
        ...prevState,
        lastOrderResult: {
          ...prevState.lastOrderResult,
          ...action.payload,
          errors: (
            lastOrderErrorsOrUndefined
              ? getOnlyExistsErrorsOrUndefined(lastOrderErrorsOrUndefined)
              : undefined
          ),
        },
      }
    }
    case OrderResultsReducerChangeLastPreviewOrderAction.type: {
      const {
        errors: lastPreviewOrderErrorsOrUndefined,
      } = action.payload
      return {
        ...prevState,
        lastOrderPreviewResult: {
          ...prevState.lastOrderPreviewResult,
          ...action.payload,
          errors: (
            lastPreviewOrderErrorsOrUndefined
              ? getOnlyExistsErrorsOrUndefined(lastPreviewOrderErrorsOrUndefined)
              : undefined
          ),
        },
      }
    }
    case OrderResultsReducerResetLastOrderErrorsAction.type: {
      if (!prevState.lastOrderResult) {
        return prevState
      }
      return {
        ...prevState,
        lastOrderResult: {
          ...prevState.lastOrderResult,
          errors: undefined,
        },
      }
    }
    case OrderResultsReducerNewOrderPendingAction.type: {
      return {
        ...prevState,
        lastOrderResult: {
          isPending: true,
          mode: action.payload,
          success: undefined,
          errors: undefined,
        },
      }
    }
    case OrderResultsReducerNewPreviewOrderPendingAction.type: {
      return {
        ...prevState,
        lastOrderPreviewResult: {
          isPending: true,
          success: undefined,
          errors: undefined,
        },
      }
    }
    case OrderResultsReducerResetLastPreviewOrderErrorsAction.type: {
      if (!prevState.lastOrderPreviewResult) {
        return prevState
      }
      return {
        ...prevState,
        lastOrderPreviewResult: {
          ...prevState.lastOrderPreviewResult,
          errors: undefined,
        },
      }
    }
    case GetCryptoRewardsOrdersListRequestAction.type: {
      if (action.meta === 'request') {
        return {
          ...prevState,
          isLoading: true,
          ordersHistoryLoadingErrors: undefined,
        }
      }
      if (action.meta === 'fail') {
        return {
          ...prevState,
          isLoading: false,
          ordersHistoryLoadingErrors: [
            ...Object.keys(action.payload.errorsMap ?? []).reduce((acc, key) => (
              [...acc, ...(action.payload.errorsMap?.[key] ?? [])]
            ), []),
            ...Object.values(action.payload.errorExtra ?? []),
            'Request failed',
          ],
        }
      }

      if (!action.payload.response) {
        return {
          ...prevState,
          isLoading: false,
          ordersHistoryLoadingErrors: undefined,
        }
      }
      return {
        ...prevState,
        isLoading: false,
        ordersHistoryLoadingErrors: undefined,
      }
    }

    case CryptoRewardsOrdersListResetErrorsAction.type: {
      return {
        ...prevState,
        ordersHistoryLoadingErrors: undefined,
      }
    }

    default:
      return prevState
  }
}
